import React, { Component, ReactNode } from 'react';

import defaultComponentDecorator from '../decorators/defaultComponentDecorator';
import defaultHrefDecorator from '../decorators/defaultHrefDecorator';
import defaultMatchDecorator from '../decorators/defaultMatchDecorator';
import defaultTextDecorator from '../decorators/defaultTextDecorator';

interface Match {
  index: number;
  lastIndex: number;
  url: string;
  text: string;
}

interface LinkifyProps {
  children: ReactNode;
  componentDecorator?: (href: string, text: string, key: number) => ReactNode;
  hrefDecorator?: (url: string) => string;
  matchDecorator?: (text: string) => Match[] | undefined;
  textDecorator?: (text: string) => string;
}

class Linkify extends Component<LinkifyProps> {
  static defaultProps = {
    componentDecorator: defaultComponentDecorator,
    hrefDecorator: defaultHrefDecorator,
    matchDecorator: defaultMatchDecorator,
    textDecorator: defaultTextDecorator,
  };

  parseString(string: string) {
    if (string === '') {
      return string;
    }

    const { matchDecorator, hrefDecorator, textDecorator, componentDecorator } =
      this.props;

    const matches = matchDecorator!(string);

    if (!matches) {
      return string;
    }

    const elements: ReactNode[] = [];

    let lastIndex = 0;

    matches.forEach((match, i) => {
      // Push preceding text if there is any
      if (match.index > lastIndex) {
        elements.push(string.substring(lastIndex, match.index));
      }

      const decoratedHref = hrefDecorator!(match.url);
      const decoratedText = textDecorator!(match.text);
      const decoratedComponent = componentDecorator!(
        decoratedHref,
        decoratedText,
        i,
      );

      elements.push(decoratedComponent);

      lastIndex = match.lastIndex;
    });

    // Push remaining text if there is any
    if (string.length > lastIndex) {
      elements.push(string.substring(lastIndex));
    }

    return elements.length === 1 ? elements[0] : elements;
  }

  parse(children: ReactNode, key: number = 0): ReactNode {
    let result: ReactNode = children;

    if (typeof children === 'string') {
      result = this.parseString(children);
    } else if (
      React.isValidElement(children) &&
      children.type !== 'a' &&
      children.type !== 'button'
    ) {
      result = React.cloneElement(
        children,
        { key } as any,
        this.parse((children.props as any).children),
      );
    } else if (Array.isArray(children)) {
      result = children.map((child, i) => this.parse(child, i));
    }

    return result;
  }

  render() {
    const { children } = this.props;

    return <>{this.parse(children)}</>;
  }
}

export default Linkify;
